import { api } from './api';

export const PATIENT_ROLES = ['PATIENT'];
export const requireClinicAdmin = (actor) => {
    if (!actor?.user?.uuid || actor.role?.name !== 'CLINIC_ADMIN' || actor.role.disabled
        || !actor.role.uuid || !actor.role.account?.uuid) throw new Error('PATIENTS_ACCESS_DENIED');
    return { 'CHEART-ROLE': actor.role.uuid };
};
const verifyTarget = (actor, target) => {
    requireClinicAdmin(actor);
    if (!target?.role_uuid || !PATIENT_ROLES.includes(target.role)
        || target.account?.uuid !== actor.role.account.uuid) throw new Error('INVALID_PATIENT_TARGET');
};
export const getPatients = async (actor, {
    disabled = false, search = '', token = null, pageSize = 10, signal
} = {}) => {
    const headers = requireClinicAdmin(actor);
    Object.assign(headers, {
        'CHEART-QUERY-ROLE': JSON.stringify(PATIENT_ROLES), 'CHEART-QUERY-DISABLED': String(disabled),
        'PAGINATION-LIMIT': pageSize
    });
    if (token) headers['PAGINATION-TOKEN'] = token;
    const query = search.trim();
    // The backend scopes clinic actors to their selected account. Do not send an account override.
    const { data, headers: pagination } = query
        ? await api.post('/cheart/users/search', { query }, { headers, signal })
        : await api.get('/cheart/users', { headers, signal });
    const total = Number(pagination['pagination-count']);
    if (!Array.isArray(data) || !Number.isInteger(total) || total < 0) throw new Error('INVALID_ACCESS_PAGE');
    for (const target of data) {
        verifyTarget(actor, target);
        if (!PATIENT_ROLES.includes(target.role) || Boolean(target.disabled) !== disabled) throw new Error('INVALID_ACCESS_PAGE');
    }
    // Keep only the administrative list projection. Never call /users/fetch for patients.
    const rows = data.map((row) => ({
        role_uuid: row.role_uuid, user_uuid: row.user_uuid, name: row.name, email: row.email,
        role: row.role, disabled: row.disabled, created: row.created, last_access: row.last_access,
        account: { uuid: row.account.uuid, name: row.account.name }
    }));
    return { rows, total, nextToken: pagination['pagination-token'] || null };
};

const findExistingAccess = async (actor, values) => {
    let token = null;
    const seen = new Set();
    let loaded = 0;
    do {
        const page = await getPatients(actor, {
            search: values.email, token, pageSize: 50
        });
        const found = page.rows.find((row) => row.email?.trim().toLowerCase() === values.email.trim().toLowerCase());
        if (found) return found;
        loaded += page.rows.length;
        if (loaded >= page.total) return null;
        token = page.nextToken;
        if (!token || seen.has(token)) throw new Error('INVALID_ACCESS_PAGE');
        seen.add(token);
    } while (token);
    return null;
};

export const createPatient = async (actor, values) => {
    const headers = requireClinicAdmin(actor);
    const existing = await findExistingAccess(actor, values);
    if (existing) return { access: existing, recovered: true };
    try {
        const { data } = await api.post('/cheart/users', {
            name: values.name.trim(), email: values.email.trim(), role: 'PATIENT',
            ...(values.phone?.trim() ? { phone: values.phone.trim() } : {})
        }, { headers });
        verifyTarget(actor, data);
        if (data.role !== 'PATIENT' || data.disabled || data.email?.trim().toLowerCase() !== values.email.trim().toLowerCase()) throw new Error('INVALID_PATIENT_TARGET');
        return { access: data, recovered: false };
    } catch (error) {
        // Creating a role commits before sending the notification email.
        const confirmed = await findExistingAccess(actor, values).catch(() => null);
        if (confirmed) return { access: confirmed, recovered: true };
        throw error;
    }
};

export const updatePatient = async (actor, target, values) => {
    verifyTarget(actor, target);
    const payload = { role_uuid: target.role_uuid, name: values.name.trim() };
    // The list endpoint does not expose phone. Only change it on explicit user request.
    if (values.updatePhone === true) payload.phone = values.phone.trim();
    return api.patch('/cheart/users', payload, { headers: requireClinicAdmin(actor) });
};

export const disablePatient = async (actor, target) => {
    verifyTarget(actor, target);
    if (target.user_uuid === actor.user.uuid) throw new Error('CANNOT_DISABLE_SELF');
    return api.delete('/cheart/users', {
        data: { role_uuid: target.role_uuid }, headers: requireClinicAdmin(actor)
    });
};

export const reactivatePatient = async (actor, target) => {
    verifyTarget(actor, target);
    return createPatient(actor, { name: target.name, email: target.email });
};
